import { composeBakedIllustrationPrompt } from "./bakedIllustration.js";
import { solidBackdropPromptLines } from "./rasterBackdrop.js";
export const SURF_RASTER_KINDS = [
    {
        id: "emphasis",
        label: "Testo con enfasi",
        description: "Solo il testo, reso come tipografia: parti in **grassetto**/*corsivo*/# titolo evidenziate con colore accento e dimensioni diverse.",
    },
    {
        id: "bullets",
        label: "Elenco puntato",
        description: "Lista illustrata con un'icona coerente per ogni voce del testo.",
    },
    {
        id: "infographic",
        label: "Infografica",
        description: "Composizione sintetica degli elementi con icone, senza dati aggiuntivi.",
    },
    {
        id: "diagram",
        label: "Diagramma",
        description: "Flusso o relazioni tra i concetti presenti nel testo.",
    },
    {
        id: "schema",
        label: "Schema",
        description: "Architettura a blocchi con etichette fedeli al testo sorgente.",
    },
    {
        id: "process",
        label: "Processo",
        description: "Step sequenziali o cicli derivati dalle voci del testo.",
    },
    {
        id: "timeline",
        label: "Linea temporale",
        description: "Sequenza temporale usando solo le etichette del testo.",
    },
    {
        id: "comparison",
        label: "Confronto",
        description: "Confronto a colonne tra le voci fornite.",
    },
    {
        id: "chart",
        label: "Grafico",
        description: "Solo se il testo contiene valori numerici espliciti.",
    },
    {
        id: "custom",
        label: "Personalizzato",
        description: "Scrivi tu il prompt; lo stile della presentazione viene comunque applicato.",
    },
    {
        id: "freeform",
        label: "Illustrazione libera",
        description: "Comportamento classico: illustrazione generica con testo integrato.",
    },
];
const KIND_SET = new Set(SURF_RASTER_KINDS.map((k) => k.id));
export function getRasterKind(layer) {
    const raw = layer.style?.rasterKind;
    if (typeof raw === "string" && KIND_SET.has(raw)) {
        return raw;
    }
    return "freeform";
}
export function setRasterKind(layer, kind) {
    return {
        ...layer,
        style: {
            ...layer.style,
            rasterKind: kind === "freeform" ? undefined : kind,
            rasterStale: true,
        },
    };
}
/** True when the source text contains explicit numeric values (for chart kind). */
export function hasExplicitNumbers(text) {
    return /\b\d+(?:[.,]\d+)?\s*%?/.test(text);
}
function kindLabel(kind) {
    switch (kind) {
        case "emphasis":
            return "emphasized typographic text";
        case "bullets":
            return "bullet list";
        case "infographic":
            return "infographic";
        case "diagram":
            return "flow diagram";
        case "schema":
            return "block schema";
        case "process":
            return "process steps";
        case "timeline":
            return "timeline";
        case "comparison":
            return "comparison layout";
        case "chart":
            return "data chart";
        case "custom":
            return "custom illustration";
        default:
            return "illustration";
    }
}
function aspectLead(aspect) {
    switch (aspect) {
        case "1:1":
            return "1:1 square presentation graphic.";
        case "2:3":
            return "2:3 portrait presentation graphic.";
        case "3:2":
            return "3:2 landscape presentation graphic.";
        default:
            return "16:9 presentation graphic.";
    }
}
/**
 * Strip "no text" style clauses: the look-only style treatment (and art direction)
 * forbid text, which would defeat a text-bearing artifact. Remove those phrases.
 */
function stripNoTextClauses(s) {
    return s
        .replace(/,?\s*\bno (readable\s+)?(text|typography|letters|words|labels|captions)\b[^,.;]*/gi, "")
        .replace(/\s{2,}/g, " ")
        .trim();
}
function visualStyleLines(style) {
    const treatment = stripNoTextClauses(style.derivedPrompt?.trim() || style.backgroundTreatment);
    return [
        // Look/style only — NO art-direction "no text" lines: this artifact MUST contain text.
        `Visual style (look only): ${treatment}`,
        `Palette mood: surface ${style.palette.surface}, accent ${style.palette.accent}`,
        "Clean flat vector style, generous spacing, high contrast for readable labels.",
    ];
}
function fidelityRules(kind) {
    const label = kindLabel(kind);
    return [
        `Render ONLY a single ${label} derived STRICTLY from the source text below.`,
        "CRITICAL: render EVERY source line as legible, correctly-spelled text INSIDE the artifact, VERBATIM — same wording, same language. Do NOT omit, drop, merge, paraphrase, translate, summarize, or reorder any line. The source text must be clearly readable in the image.",
        "Do NOT add any text that is not in the source: no invented title, header, subtitle, caption, legend, logo, watermark, or page number.",
        "Do NOT invent data, numbers, percentages, dates, statistics, or facts not present in the text.",
    ];
}
function kindSpecialization(kind, items) {
    switch (kind) {
        case "bullets":
            return [
                "Format: clean illustrated bullet list, one row per source item.",
                "One coherent icon per row; do not add numbering unless present in the source.",
            ].join(" ");
        case "chart":
            return [
                "ONLY if the source contains explicit numeric values; otherwise DO NOT fabricate a chart.",
                "Use only numbers verbatim from the source; never invent axes, scales, or values.",
            ].join(" ");
        case "diagram":
            return "Flow or relationship diagram: nodes and connectors match source items only; no extra nodes.";
        case "schema":
            return "Block architecture schema: blocks labeled with source items only; no extra blocks.";
        case "process":
            return "Sequential process or cycle: steps labeled with source items; no extra steps.";
        case "timeline":
            return "Horizontal or vertical timeline: milestones labeled with source items only.";
        case "comparison":
            return "Side-by-side comparison columns using source labels only; no extra categories.";
        case "infographic":
            return "Synthetic infographic composition of source items with icons; no data not in the text.";
        default:
            return "";
    }
}
function sourceItemsBlock(items, sourceText) {
    const lines = [
        "Source lines — render ALL of them as readable text inside the artifact, verbatim:",
    ];
    if (items.title) {
        lines.push(`- "${items.title.replace(/"/g, '\\"')}"`);
    }
    for (const bullet of items.bullets ?? []) {
        lines.push(`- "${bullet.replace(/"/g, '\\"')}"`);
    }
    if (!items.title && !(items.bullets?.length ?? 0)) {
        lines.push(`- "${sourceText.replace(/"/g, '\\"')}"`);
    }
    return lines.join("\n");
}
/** Parse markdown emphasis from vector text → plain text (markers stripped) + emphasized spans. */
export function parseMarkdownEmphasis(md) {
    const headings = [];
    let body = md.replace(/^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/gm, (_m, h) => {
        headings.push(h.trim());
        return h.trim();
    });
    const strong = [];
    body = body.replace(/\*\*([^*\n]+)\*\*|__([^_\n]+)__/g, (_m, a, b) => {
        const t = (a ?? b ?? "").trim();
        if (t)
            strong.push(t);
        return t;
    });
    const em = [];
    body = body.replace(/\*([^*\n]+)\*|(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])/g, (_m, a, b) => {
        const t = (a ?? b ?? "").trim();
        if (t)
            em.push(t);
        return t;
    });
    return { plain: body.replace(/[ \t]{2,}/g, " ").trim(), headings, strong, em };
}
function quoteList(items) {
    return items.map((s) => `"${s.replace(/"/g, '\\"')}"`).join(", ");
}
function composeEmphasisTextPrompt(opts) {
    const { sourceText, style, backdropHex, aspect } = opts;
    const { plain, headings, strong, em } = parseMarkdownEmphasis(sourceText || "");
    const base = style.palette.text;
    const accent = style.palette.accent;
    const lines = [
        aspectLead(aspect),
        "Render ONLY typographic text — no icons, no diagram, no chart, no illustration, no decorative shapes or frames.",
        "CRITICAL: render the text below VERBATIM — exactly these words, correct spelling, same language. Do NOT add or omit any word.",
        `Base text in color ${base}. Emphasized parts in accent color ${accent}, larger size and heavier weight, to create clear visual hierarchy.`,
        headings.length ? `Largest headline size, accent ${accent}: ${quoteList(headings)}.` : "",
        strong.length ? `Strong emphasis (accent ${accent}, large bold): ${quoteList(strong)}.` : "",
        em.length ? `Secondary emphasis (accent ${accent}, medium/italic): ${quoteList(em)}.` : "",
        headings.length || strong.length || em.length
            ? ""
            : `Emphasize the 1–3 most important words in accent ${accent}, larger and bolder; rest in ${base}.`,
        `Full text to render, verbatim: "${plain.replace(/"/g, '\\"')}"`,
        "Clean balanced typographic composition, generous spacing, high contrast, no background pattern or texture.",
    ].filter(Boolean);
    if (backdropHex)
        lines.push(...solidBackdropPromptLines(backdropHex));
    return lines.join(" ");
}
export function composeRasterArtifactPrompt(opts) {
    const { kind, sourceText, items, style, backdropHex, aspect, subject, slideIndex } = opts;
    if (kind === "freeform") {
        return composeBakedIllustrationPrompt(style, subject ?? "presentation content", items, slideIndex ?? 0, false, aspect, backdropHex);
    }
    if (kind === "emphasis") {
        return composeEmphasisTextPrompt(opts);
    }
    if (kind === "custom") {
        const instruction = opts.customPrompt?.trim() || "Illustrate the source content below.";
        const parts = [
            aspectLead(aspect),
            `Custom instruction (authoritative): ${instruction}`,
            // Global presentation style is ALWAYS applied, even for custom.
            ...visualStyleLines(style),
            sourceItemsBlock(items, sourceText),
            "Do NOT invent data, numbers, dates, or statistics not present in the source.",
        ];
        if (backdropHex)
            parts.push(...solidBackdropPromptLines(backdropHex));
        return parts.join(" ");
    }
    const parts = [
        aspectLead(aspect),
        ...fidelityRules(kind),
        kindSpecialization(kind, items),
        sourceItemsBlock(items, sourceText),
        ...visualStyleLines(style),
    ];
    if (backdropHex) {
        parts.push(...solidBackdropPromptLines(backdropHex));
    }
    return parts.join(" ");
}
